import React from "react";
import { getVal } from "oolib";

import { usePrevious } from "../customHooks";

/**
 * Hidden-value cleanup for conditionally-displayed blocks, collab edition.
 *
 * The edit form deletes a block's saved value when its displayCondition flips
 * false (a hidden answer must not persist). Under collab EVERY client sees
 * the flip at the same moment, so exactly ONE of them performs the deletion:
 * the client holding the lock claim on the CONTROLLING field — the flip only
 * ever happens because that client changed it. Everyone else just hides.
 *
 * The actor test uses the client's LOCAL lock claim (hasLockClaim), not the
 * broadcast locks state: the flip renders in the same tick as the
 * controlling click, before the server's locks:state broadcast round-trips.
 * condition.HELPER_actualValuePath is the controlling BLOCK's exact
 * valuePath (TCI stamps it); condition.valuePath may carry a ".value"
 * suffix, so the broadcast-state fallback prefix-matches instead.
 *
 * Every uncertain path SKIPS — the failure mode is "stale hidden value
 * survives" (the pre-fix status quo), never a wrong deletion:
 *  - first render is never a transition (no deletes on doc open)
 *  - the hidden field being locked by another client skips
 *  - not holding the controlling field's claim skips
 *
 * The deletion itself is a normal lock-bracketed collab write (acquire →
 * null → release), so it flushes and every peer converges on it. Runs as an
 * effect — unlike the edit form's render-time content mutation, which a
 * collab surface must not imitate.
 */
export const useCollabConditionalValueCleanup = ({
  enabled,
  collabEdit,
  block,
  content,
  displayConditionsResult,
}) => {
  const prevDisplayConditionsResult = usePrevious(displayConditionsResult);

  React.useEffect(() => {
    if (!enabled || !collabEdit) return;
    if (!(prevDisplayConditionsResult === true && displayConditionsResult === false)) return;
    if (!block.valuePath || !getVal(content, block.valuePath)) return;

    const locks = collabEdit.locks || {};
    const hiddenLock = locks[block.valuePath];
    if (hiddenLock && hiddenLock.socketId !== collabEdit.socketId) return;

    const iAmTheActor = (block.displayConditions || []).some((c) => {
      if (!c.valuePath || c.operator === "HIDE") return false;
      const controllingBlockPath = String(c.HELPER_actualValuePath || c.valuePath).replace(
        "$PARENT_CONTENT.",
        ""
      );
      if (collabEdit.hasLockClaim?.(controllingBlockPath)) return true;
      // fallback for HELPER-less legacy conditions: broadcast state, prefix-matched
      const controlling = String(c.valuePath).replace("$PARENT_CONTENT.", "");
      return Object.entries(locks).some(
        ([lockPath, lock]) =>
          lock.socketId === collabEdit.socketId &&
          (controlling === lockPath || controlling.startsWith(lockPath + "."))
      );
    });
    if (!iAmTheActor) return;

    collabEdit.acquireLock(block.valuePath);
    collabEdit.setField(block.valuePath, null);
    collabEdit.releaseLock(block.valuePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayConditionsResult]);
};
